import { useAuth } from 'auth';
import { Button, FieldSelect, FormControl } from 'components/atoms';
import { Modal } from 'components/molecules';
import { useConfig } from 'config';
import { useFetch } from 'hooks';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ButtonContainer, EditMonitoringForm } from './EditMonitoringModal.styles';
import { EditMonitoringModalTypes } from './types';
import Loading from 'components/atoms/loading';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { GatewaySubscriptionTypeNames } from 'auth/constants/subscriptions';

const EditMonitoringModal: FC<EditMonitoringModalTypes> = ({
  isOpen,
  onClose,
  hasMonitoring,
  ongoingScreeningSearchTypeId,
  previousOngoingScreeningSearchTypeId,
  screeningContainerId,
  reload,
  dueDiligenceTypes,
  screeningDueDiligenceType,
}: EditMonitoringModalTypes) => {
  const { tokenInfo } = useAuth();
  const { vantageWebApi } = useConfig();

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(
      yup.object().shape({
        monitoring: yup.string().required('This field is required'),
        monitoringScreeningSettings: yup
          .number()
          .typeError('Please select a monitoring setting')
          .nullable()
          .when('monitoring', {
            is: 'enabled',
            then: yup
              .number()
              .typeError('Please select a monitoring setting')
              .required('Please select a monitoring setting'),
          }),
      })
    ),
    defaultValues: {
      monitoring: useMemo(() => (hasMonitoring ? 'enabled' : 'disabled'), [hasMonitoring]),
      monitoringScreeningSettings: useMemo(() => {
        if (screeningDueDiligenceType === GatewaySubscriptionTypeNames.ManagedServiceScreening) {
          return hasMonitoring
            ? ongoingScreeningSearchTypeId
            : previousOngoingScreeningSearchTypeId;
        }
        return hasMonitoring ? ongoingScreeningSearchTypeId : null;
      }, [
        hasMonitoring,
        ongoingScreeningSearchTypeId,
        previousOngoingScreeningSearchTypeId,
        screeningDueDiligenceType,
      ]),
    },
  });

  const isManagedServiceScreening =
    screeningDueDiligenceType === GatewaySubscriptionTypeNames.ManagedServiceScreening;

  useEffect(() => {
    let monitoringScreeningSettings;

    if (isManagedServiceScreening) {
      monitoringScreeningSettings = hasMonitoring
        ? ongoingScreeningSearchTypeId
        : previousOngoingScreeningSearchTypeId;
    } else {
      monitoringScreeningSettings = hasMonitoring ? ongoingScreeningSearchTypeId : null;
    }

    reset({
      monitoring: hasMonitoring ? 'enabled' : 'disabled',
      monitoringScreeningSettings: monitoringScreeningSettings,
    });
  }, [
    hasMonitoring,
    ongoingScreeningSearchTypeId,
    previousOngoingScreeningSearchTypeId,
    screeningDueDiligenceType,
    isManagedServiceScreening,
    reset,
  ]);

  const settingsDisabled = watch('monitoring') === 'disabled';
  const {
    data: submitData,
    loading: submitLoading,
    trigger: triggerSubmit,
    reset: submitReset,
  } = useFetch(`${vantageWebApi}/onlinescreening/${screeningContainerId}/edit-monitoring`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
    lazy: true,
  });

  let monitoringScreeningSettings = [];
  const onCloseCall = useCallback(() => {
    submitReset();
    onClose();
  }, [onClose, submitReset]);

  if (dueDiligenceTypes && dueDiligenceTypes?.length > 0) {
    const screeningDuediligence = dueDiligenceTypes.filter((d) => d.name === 'Screening');
    const monitoringScreeningSearchTypes = screeningDuediligence[0].dueDiligenceTypes
      ?.find(
        ({ name }) => name === (isManagedServiceScreening ? 'Managed Service' : 'Self Service')
      )
      ?.ongoingScreeningSearchTypes?.map(({ id, description }) => ({
        name: description,
        value: id,
      }));

    monitoringScreeningSettings =
      dueDiligenceTypes &&
      screeningDuediligence.length > 0 &&
      screeningDuediligence[0].dueDiligenceTypes
        ? monitoringScreeningSearchTypes
        : [];
  }

  const submitMonitoringChange = () => {
    const values = getValues();
    let screeningTypeId = Number(values.monitoringScreeningSettings);

    if (settingsDisabled && isManagedServiceScreening) {
      screeningTypeId = null;
    }

    triggerSubmit({
      body: JSON.stringify({
        ongoingScreeningSearchTypeId: screeningTypeId,
        monitoring: values.monitoring === 'enabled',
      }),
    });
  };

  useEffect(() => {
    if (submitData) {
      reload();
      if (isOpen) {
        onCloseCall();
      }
    }
  }, [submitData, reload, onCloseCall, isOpen]);

  const isMonitoringSettingsSelectDisabled = () => {
    return (
      settingsDisabled ||
      (isManagedServiceScreening &&
        (previousOngoingScreeningSearchTypeId !== null || ongoingScreeningSearchTypeId !== null))
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseCall} title="Edit monitoring">
      {!submitLoading && (
        <EditMonitoringForm onSubmit={handleSubmit(submitMonitoringChange)}>
          <FormControl errorMessage={errors?.monitoring?.message}>
            <FieldSelect
              ariaLabel="Monitoring"
              id="monitoring"
              label="Monitoring"
              placeholder="select..."
              options={[
                {
                  name: 'Enabled',
                  value: 'enabled',
                },
                {
                  name: 'Disabled',
                  value: 'disabled',
                },
              ]}
              register={register}
              fullWidth
              valid={!errors?.monitoring?.message}
            />
          </FormControl>
          <FormControl errorMessage={errors?.monitoringScreeningSettings?.message}>
            <FieldSelect
              ariaLabel="Monitoring screening settings"
              id="monitoringScreeningSettings"
              label="Monitoring screening settings"
              placeholder="select..."
              options={monitoringScreeningSettings}
              register={register}
              disabled={isMonitoringSettingsSelectDisabled()}
              fullWidth
              valid={!errors?.monitoringScreeningSettings?.message}
            />
          </FormControl>
          <ButtonContainer>
            <Button type="submit" secondary>
              Submit
            </Button>
            <Button type="button" text onClick={onCloseCall}>
              Cancel
            </Button>
          </ButtonContainer>
        </EditMonitoringForm>
      )}
      {submitLoading && <Loading />}
    </Modal>
  );
};

export default EditMonitoringModal;
